import React, { useState } from 'react';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import { apiClient } from '@/lib/api/client';
import DetailModal from '../common/DetailModal';

interface RiskMatrixProps {
  risks: any;
}

const RiskMatrix: React.FC<RiskMatrixProps> = ({ risks }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any>(null);
  const [modalTitle, setModalTitle] = useState('');
  const [modalSubtitle, setModalSubtitle] = useState('');

  const handleRiskClick = async (severity: string, count: number) => {
    if (count === 0) return;

    try {
      const severityParam = severity.toUpperCase().replace(' ', '_');
      const response = await apiClient.get('/dashboard/compliance/risks', {
        params: { severity: severityParam }
      });

      const displayName = severity === 'best_practice' ? 'Best Practice' :
                          severity.charAt(0).toUpperCase() + severity.slice(1);

      setModalTitle(`${displayName} Risks`);
      setModalSubtitle(`${count} ${displayName.toLowerCase()} issues detected`);
      setModalData(response.data);
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch risk details:', error);
      setModalData({ items: [], total_count: 0 });
      setModalOpen(true);
    }
  };
  const bySeverity = risks?.by_severity || {};
  const totalRisks = risks?.total || 0;

  const severityConfig = {
    critical: {
      color: 'bg-red-500',
      lightColor: 'bg-red-100 dark:bg-red-900/20',
      textColor: 'text-red-600 dark:text-red-400',
      icon: <AlertTriangle className="w-5 h-5" />
    },
    high: {
      color: 'bg-orange-500',
      lightColor: 'bg-orange-100 dark:bg-orange-900/20',
      textColor: 'text-orange-600 dark:text-orange-400',
      icon: <AlertCircle className="w-5 h-5" />
    },
    medium: {
      color: 'bg-yellow-500',
      lightColor: 'bg-yellow-100 dark:bg-yellow-900/20',
      textColor: 'text-yellow-600 dark:text-yellow-400',
      icon: <Info className="w-5 h-5" />
    },
    low: {
      color: 'bg-blue-500',
      lightColor: 'bg-blue-100 dark:bg-blue-900/20',
      textColor: 'text-blue-600 dark:text-blue-400',
      icon: <Info className="w-5 h-5" />
    },
    best_practice: {
      color: 'bg-green-500',
      lightColor: 'bg-green-100 dark:bg-green-900/20',
      textColor: 'text-green-600 dark:text-green-400',
      icon: <Info className="w-5 h-5" />
    }
  };

  const getMaxCount = () => {
    return Math.max(...Object.values(bySeverity).map((v: any) => v || 0), 1);
  };

  const maxCount = getMaxCount();

  return (
    <div className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Risk Assessment Matrix
      </h3>

      {totalRisks > 0 ? (
        <>
          {/* Risk bars visualization */}
          <div className="space-y-3 mb-6">
            {Object.entries(severityConfig).map(([severity, config]) => {
              const count = bySeverity[severity] || 0;
              const percentage = (count / maxCount) * 100;

              return (
                <motion.div
                  key={severity}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`relative ${count > 0 ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 p-2 -m-2 rounded-lg transition-colors' : ''}`}
                  onClick={() => handleRiskClick(severity, count)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={config.textColor}>
                        {config.icon}
                      </span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {severity === 'best_practice' ? 'Best Practice' : severity.charAt(0).toUpperCase() + severity.slice(1)}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {count}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-6">
                    <motion.div
                      className={`${config.color} h-6 rounded-full flex items-center justify-end pr-2`}
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.8, delay: 0.1 }}
                    >
                      {count > 0 && (
                        <span className="text-xs font-medium text-white">
                          {((count / totalRisks) * 100).toFixed(0)}%
                        </span>
                      )}
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => handleRiskClick('critical', bySeverity.critical || 0)}
              className="text-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
              disabled={!bySeverity.critical}
            >
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {bySeverity.critical || 0}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Critical</div>
            </button>
            <button
              onClick={() => handleRiskClick('high', bySeverity.high || 0)}
              className="text-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
              disabled={!bySeverity.high}
            >
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {bySeverity.high || 0}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">High</div>
            </button>
            <button
              onClick={() => handleRiskClick('ALL', totalRisks)}
              className="text-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
              disabled={totalRisks === 0}
            >
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalRisks}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Total</div>
            </button>
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p>No risks detected</p>
          <p className="text-sm mt-1">All systems operating normally</p>
        </div>
      )}
      <DetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        subtitle={modalSubtitle}
        data={modalData}
        type="risk"
      />
    </div>
  );
};

export default RiskMatrix;